/**
 * PhotoPicker — choose or capture a photo for a child.
 *
 * This component NEVER uploads anywhere. It reads a blob from a file
 * input or an in-page camera capture (getUserMedia), hands it to the
 * parent via `onPhotoSelected`, and otherwise does nothing network-
 * related. The caller is responsible for storing the blob in
 * IndexedDB via `lib/photos.ts`. See `CLAUDE.md` for why this line
 * matters.
 *
 * Two capture modes:
 * 1. **File input** — `<input type="file" accept="image/*">`. On
 *    mobile, tapping this opens the OS photo picker; on desktop it
 *    opens the file dialog. Works everywhere.
 * 2. **Camera** — click "Use camera", we call `getUserMedia`, show
 *    a live preview in a modal, user snaps a photo via a shutter
 *    button. On permission denial or no camera we fall back to the
 *    file input with a matter-of-fact message.
 *
 * Caption copy ("This photo stays on this device") is required, not
 * optional — the privacy story is a feature, not a disclaimer.
 */

import { useEffect, useRef, useState, type ChangeEvent } from "react";

interface PhotoPickerProps {
  /** Current photo URL (object URL from `usePhotoURL`), or null. */
  currentPhotoUrl: string | null;
  /** Called when the user picks a new photo. */
  onPhotoSelected: (blob: Blob) => void;
  /** Called when the user clicks "Remove photo". */
  onPhotoRemoved: () => void;
  /** Optional label to display above the picker. */
  label?: string;
}

export function PhotoPicker({
  currentPhotoUrl,
  onPhotoSelected,
  onPhotoRemoved,
  label = "Photo",
}: PhotoPickerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file === undefined) return;
    onPhotoSelected(file);
    // Reset the input so selecting the same file again re-fires onChange.
    event.target.value = "";
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="font-sans text-eyebrow uppercase text-ink-muted font-semibold">
        {label}
      </div>

      <div className="flex items-start gap-5">
        <PhotoThumbnail url={currentPhotoUrl} />

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="self-start px-4 py-2 rounded-md font-sans text-[13px] font-medium text-ink bg-surface border border-edge hover:border-coral-deep hover:text-coral-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
          >
            {currentPhotoUrl === null ? "Choose photo" : "Replace photo"}
          </button>

          <button
            type="button"
            onClick={() => setIsCameraOpen(true)}
            className="self-start px-4 py-2 rounded-md font-sans text-[13px] font-medium text-ink bg-surface border border-edge hover:border-coral-deep hover:text-coral-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
          >
            Use camera
          </button>

          {currentPhotoUrl !== null && (
            <button
              type="button"
              onClick={onPhotoRemoved}
              className="self-start px-4 py-2 rounded-md font-sans text-[13px] text-ink-muted hover:text-coral-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            >
              Remove photo
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={handleFileChange}
          />
        </div>
      </div>

      <p className="font-sans text-[12px] italic text-ink-muted max-w-[440px]">
        This photo stays on this device. Make It Count never uploads photos
        of children to the server.
      </p>

      {isCameraOpen && (
        <CameraModal
          onCapture={(blob) => {
            onPhotoSelected(blob);
            setIsCameraOpen(false);
          }}
          onClose={() => setIsCameraOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thumbnail
// ---------------------------------------------------------------------------

function PhotoThumbnail({ url }: { url: string | null }) {
  return (
    <div
      className="w-24 h-24 rounded-md border border-edge bg-surface overflow-hidden flex items-center justify-center"
      aria-hidden={url === null}
    >
      {url === null ? (
        <SilhouettePlaceholder />
      ) : (
        // eslint-disable-next-line jsx-a11y/alt-text
        <img src={url} alt="" className="w-full h-full object-cover" />
      )}
    </div>
  );
}

function SilhouettePlaceholder() {
  return (
    <svg
      viewBox="0 0 96 96"
      className="w-12 h-12 text-ink-muted/50"
      aria-hidden="true"
    >
      <circle cx="48" cy="36" r="16" fill="currentColor" />
      <path
        d="M16 84c0-17.7 14.3-32 32-32s32 14.3 32 32"
        fill="currentColor"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Camera modal — getUserMedia + <video> + canvas capture
// ---------------------------------------------------------------------------

interface CameraModalProps {
  onCapture: (blob: Blob) => void;
  onClose: () => void;
}

function CameraModal({ onCapture, onClose }: CameraModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<"starting" | "ready" | "error">(
    "starting",
  );
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    let cancelled = false;

    async function start() {
      if (typeof navigator === "undefined" || navigator.mediaDevices === undefined) {
        setStatus("error");
        setErrorMessage(
          "This browser doesn't support in-app camera capture. Use the Replace Photo button to pick a file instead.",
        );
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current !== null) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {
            // play() can reject on autoplay policy quirks — that's fine
          });
        }
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setErrorMessage(
          err instanceof Error && err.name === "NotAllowedError"
            ? "Camera permission denied. Use Replace Photo to pick a file instead."
            : "Couldn't start the camera. Use Replace Photo to pick a file instead.",
        );
      }
    }

    void start();

    return () => {
      cancelled = true;
      if (streamRef.current !== null) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const handleCapture = () => {
    const video = videoRef.current;
    if (video === null || status !== "ready") return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (blob !== null) {
          onCapture(blob);
        }
      },
      "image/jpeg",
      0.92,
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-ink/70 px-0 md:px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Camera capture"
    >
      <div className="w-full max-w-[560px] rounded-t-lg md:rounded-lg bg-surface p-4 md:p-6 flex flex-col gap-4 shadow-xl">
        <div className="font-serif text-title text-ink">Take a photo</div>

        {status === "error" ? (
          <p className="font-sans text-[14px] text-ink-soft">{errorMessage}</p>
        ) : (
          <div className="w-full aspect-[4/3] rounded-md bg-ink/10 overflow-hidden flex items-center justify-center">
            {status === "starting" && (
              <div className="font-sans text-[13px] italic text-ink-muted">
                Starting camera…
              </div>
            )}
            <video
              ref={videoRef}
              className={`w-full h-full object-cover ${status === "ready" ? "" : "hidden"}`}
              playsInline
              muted
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md font-sans text-[13px] text-ink-muted hover:text-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={status !== "ready"}
            onClick={handleCapture}
            className="px-5 py-2 rounded-md font-sans text-[13px] font-semibold text-white shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
              boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
            }}
          >
            Capture
          </button>
        </div>
      </div>
    </div>
  );
}
